// ============================================================================
// STATISTIQUES › VENDEURS — un univers à trois onglets (Résumé · Reporting vendeurs · Vendeurs par période). Réf.
// maquette du back-office (« UNIVERS STATISTIQUES · VENDEURS ») et les décisions S2, S3 de son bilan.
//
// ⚠️ L'ÉCRAN DESSINE, IL NE DÉFINIT AUCUN NOMBRE : chiffres, parts, écarts, profils et jours viennent du serveur
//    (`sales/stats-sellers.ts`). Ce qui se calcule ici est de la GÉOMÉTRIE (positions, tracés, teintes) — et le cumul de
//    la course au chiffre, une somme courante des seaux servis, comme l'évolution du Résumé des ventes.
// ⚠️ LE VENDEUR EST L'OPÉRATEUR DE LA PIÈCE, PAR SON IDENTIFIANT (S2). « Repos » n'existe pas : un jour sans vente se dit
//    « aucune vente ». Les objectifs vendeurs attendent leur tranche (S3) : ni tuile ni barre.
// ⚠️ LE REPORTING VENDEURS EST LE REPORTING VENTES, VENTILÉ PAR VENDEUR : même tableau, mêmes colonnes (le réglage est
//    partagé), sans ce qui n'a pas de vendeur — stock, rétrocessions, commandes. Ses vues enregistrées sont les siennes.
// ⚠️ VENDEURS PAR PÉRIODE ÉCLATE UNE MESURE DE LA VENTE DANS LE TEMPS (`stats-vendeurs-periodes.tsx`) : sans comparaison ;
//    une comparaison choisie ailleurs reste celle des autres onglets.
// ============================================================================
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { EmptyState, Icon, Tabs, Trans, showToast, useChartTip } from '@orgazone/ui-web';
import type { IconName } from '@orgazone/ui-tokens';
import { api, type SalesReportView, type SalesSellerRow, type SalesSellersView, type SellerPeriodsView, type StockPeriodGrain } from '../api';
import { key, nombre, t, type Translatable } from '../i18n';
import { currentLang } from '../lang';
import { useStoreScope } from '../store-context';
import { useAncreDuJour } from '../periode';
import { StatsBar, StatsFixedLinesRow, defaultSettings, dimLabel, isoOf, rangeOf, type LevelDim, type StatsSettings } from './stats-bar';
import { dTx, eur0, pc } from './stats-format';
import {
  CARD, CARD_ROW, CardHead, EMPTY, KPI_ROW, KPI_TILE, KpiCard, KpiLabel, MONO_LABEL, Seg, TONES, axisLabel, bucketLabels, chip, monotonePath,
  niceTop, weekdayName, type Tone,
} from './stats-resume';
import {
  REPORT_DEFAULT_SORT, ReportColumnsButton, ReportDisplayToggle, StatsReportTable, exportReport, reportLegend, useSellerReportColumns,
  type ReportDisplay, type ReportSort,
} from './stats-reporting';
import { StatsViewsButton, describeView, keptColumns, keptSort, type StatsViewAdapter } from './stats-views';
import { ReadFailed, asOfNote, reportScope } from './stats-ventes';
import {
  DEFAULT_SELLER_PERIOD_MEASURE, SELLER_PERIOD_MEASURES, SELLER_PERIODS_DEFAULT_SORT, SellerPeriodsControls, SellerPeriodsTable, exportSellerPeriods,
  type SellerPeriodMeasure,
} from './stats-vendeurs-periodes';
import type { StatsSort } from './stats-grid';

/** Le seul niveau du Reporting vendeurs — il se nomme et se décrit comme un niveau, mais ne se change pas. */
const SELLER_DIMS: LevelDim[] = [{ k: 'seller', l: key('Vendeur'), ic: 'user' }];

type TabId = 'resume' | 'reporting' | 'periodes';
const TABS: { id: TabId; label: Translatable; icon: IconName }[] = [
  { id: 'resume', label: key('Résumé'), icon: 'dashboard' },
  { id: 'reporting', label: key('Vendeurs reporting'), icon: 'table' },
  { id: 'periodes', label: key('Vendeurs par période'), icon: 'calendar' },
];

type Phase = { phase: 'idle' } | { phase: 'loading' } | { phase: 'ready' } | { phase: 'failed'; message: string };

/** `AAAA-MM-JJ` → `AAAAMMJJ`, la forme que le serveur lit. */
const ymd = (iso: string): string => iso.replace(/-/g, '');

/** La teinte d'un vendeur, par son rang : la même partout (classement, profil, course, jours). */
const SELLER_TONES: readonly Tone[] = ['teal', 'violet', 'blue', 'amber', 'rose'];
export const toneOf = (i: number): Tone => SELLER_TONES[i % SELLER_TONES.length]!;
/** Les initiales d'un vendeur (« Alice Martin » → « AM »). */
const initials = (name: string): string => name.split(/\s+/).filter(Boolean).map((w) => w[0]!).join('').slice(0, 2).toUpperCase();

/** L'univers Vendeurs : ses onglets, la barre partagée, le Résumé. */
export function StatsVendeurs() {
  const [params, setParams] = useSearchParams();
  const tab = (TABS.find((x) => x.id === params.get('onglet'))?.id ?? 'resume') as TabId;
  const scope = useStoreScope();
  const gln = scope.view?.active?.gln ?? null;
  const ancre = useAncreDuJour();
  const today = isoOf(ancre);
  const [settings, setSettings] = useState<StatsSettings>(() => defaultSettings(ancre));
  const [view, setView] = useState<SalesSellersView | null>(null);
  const [status, setStatus] = useState<Phase>({ phase: 'idle' });
  const shown = useRef(false);
  const busy = status.phase === 'loading';
  // Reporting vendeurs : son affichage et son tri survivent au passage d'un onglet à l'autre.
  const [display, setDisplay] = useState<ReportDisplay>('a');
  const [sort, setSort] = useState<ReportSort>(REPORT_DEFAULT_SORT);
  const [report, setReport] = useState<SalesReportView | null>(null);
  const [reportStatus, setReportStatus] = useState<Phase>({ phase: 'idle' });
  const reportShown = useRef(false);
  const reportBusy = reportStatus.phase === 'loading';
  const columns = useSellerReportColumns(tab === 'reporting', (message) => showToast(t('Réglage non mémorisé : {v}', { v: message }), 'err'));
  // Vendeurs par période : son grain, sa mesure et son tri survivent eux aussi au passage d'un onglet à l'autre.
  const [grain, setGrain] = useState<StockPeriodGrain>('m');
  const [measure, setMeasure] = useState<SellerPeriodMeasure>(DEFAULT_SELLER_PERIOD_MEASURE);
  const [periodSort, setPeriodSort] = useState<StatsSort>(SELLER_PERIODS_DEFAULT_SORT);
  const [periods, setPeriods] = useState<SellerPeriodsView | null>(null);
  const [periodStatus, setPeriodStatus] = useState<Phase>({ phase: 'idle' });
  const periodsShown = useRef(false);
  const periodBusy = periodStatus.phase === 'loading';
  /** Un autre grain rend le tri du Total : un tri posé sur une période disparue ne décrit plus rien. */
  const changeGrain = (next: StockPeriodGrain) => { setGrain(next); setPeriodSort(SELLER_PERIODS_DEFAULT_SORT); };

  /**
   * LES RÉGLAGES DE LA BARRE, partagés par les onglets. Choisir une comparaison montre l'ÉCART, l'arrêter revient à la
   * période — comme au Reporting ventes.
   */
  const changeSettings = (next: StatsSettings) => {
    if (settings.b === null && next.b !== null) setDisplay('d');
    if (next.b === null) setDisplay('a');
    setSettings(next);
  };
  const a = rangeOf(settings.a);
  const b = settings.b ? rangeOf(settings.b) : null;
  const request = useMemo(() => ({
    from: ymd(a.from), to: ymd(a.to), ...(b ? { cmpFrom: ymd(b.from), cmpTo: ymd(b.to) } : {}),
    q: settings.q.trim(), filters: settings.filters,
  }), [a.from, a.to, b?.from, b?.to, settings.q, settings.filters]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!gln || tab !== 'resume' || !request.from || !request.to) { setStatus({ phase: 'idle' }); return; }
    let alive = true;
    // ⚠️ Le voile tombe tout de suite, l'appel attend la fin de la frappe (même règle que les Ventes).
    setStatus({ phase: 'loading' });
    const timer = setTimeout(() => {
      api.salesSellers(request)
        .then((v) => { if (!alive) return; shown.current = true; setView(v); setStatus({ phase: 'ready' }); })
        .catch((e: unknown) => {
          if (!alive) return;
          // ⚠️ Les chiffres précédents sont JETÉS : garder ceux d'une période qu'on vient de quitter mentirait.
          setView(null);
          setStatus({ phase: 'failed', message: e instanceof Error ? e.message : String(e) });
        });
    }, shown.current ? 250 : 0);
    return () => { alive = false; clearTimeout(timer); };
  }, [gln, tab, request]);

  useEffect(() => {
    if (!gln || tab !== 'reporting' || !request.from || !request.to) { setReportStatus({ phase: 'idle' }); return; }
    let alive = true;
    // ⚠️ Le voile tombe tout de suite, l'appel attend la fin de la frappe (même règle que le Reporting ventes).
    setReportStatus({ phase: 'loading' });
    const timer = setTimeout(() => {
      api.sellersReport(request)
        .then((v) => { if (!alive) return; reportShown.current = true; setReport(v); setReportStatus({ phase: 'ready' }); })
        .catch((e: unknown) => {
          if (!alive) return;
          // ⚠️ Le tableau précédent est JETÉ : garder les chiffres d'une période qu'on vient de quitter mentirait.
          setReport(null);
          setReportStatus({ phase: 'failed', message: e instanceof Error ? e.message : String(e) });
        });
    }, reportShown.current ? 250 : 0);
    return () => { alive = false; clearTimeout(timer); };
  }, [gln, tab, request]);

  // ⚠️ AUCUNE COMPARAISON N'EST ENVOYÉE : l'onglet n'en a pas, celle de la barre reste celle des autres onglets.
  const periodRequest = useMemo(() => ({
    from: ymd(a.from), to: ymd(a.to), q: settings.q.trim(), filters: settings.filters, grain,
  }), [a.from, a.to, settings.q, settings.filters, grain]);

  useEffect(() => {
    if (!gln || tab !== 'periodes' || !periodRequest.from || !periodRequest.to) { setPeriodStatus({ phase: 'idle' }); return; }
    let alive = true;
    // ⚠️ Le voile tombe tout de suite, l'appel attend la fin de la frappe (même règle que le Reporting vendeurs).
    setPeriodStatus({ phase: 'loading' });
    const timer = setTimeout(() => {
      api.sellerPeriods(periodRequest)
        .then((v) => { if (!alive) return; periodsShown.current = true; setPeriods(v); setPeriodStatus({ phase: 'ready' }); })
        .catch((e: unknown) => {
          if (!alive) return;
          // ⚠️ Le tableau précédent est JETÉ : garder les chiffres d'une période qu'on vient de quitter mentirait.
          setPeriods(null);
          setPeriodStatus({ phase: 'failed', message: e instanceof Error ? e.message : String(e) });
        });
    }, periodsShown.current ? 250 : 0);
    return () => { alive = false; clearTimeout(timer); };
  }, [gln, tab, periodRequest]);

  /**
   * LES VUES DE VENDEURS PAR PÉRIODE : la période, les filtres, le grain, la mesure et le tri. ⚠️ Elles ne comparent pas :
   * appliquer l'une laisse la comparaison des autres onglets en place.
   */
  const periodViews: StatsViewAdapter = {
    snapshot: () => ({ a: settings.a, q: settings.q, filters: settings.filters, axes: ['seller'], sort: periodSort, grain, measures: [measure] }),
    apply: (v) => {
      setSettings((cur) => ({ a: v.a, b: cur.b, q: v.q, filters: v.filters }));
      if (v.grain) setGrain(v.grain);
      let lost = 0;
      // ⚠️ UNE MESURE QUE L'ONGLET NE CONNAÎT PLUS N'EST PAS APPLIQUÉE : la mesure affichée reste, et l'écart est compté.
      const m = v.measures?.[0];
      if (m && SELLER_PERIOD_MEASURES.some((x) => x.k === m)) setMeasure(m as SellerPeriodMeasure); else lost += 1;
      // ⚠️ LES COLONNES SONT DES PÉRIODES : elles ne se connaissent qu'une fois lues, le tri est repris tel quel.
      const k = keptSort(v.sort, 1, null, SELLER_PERIODS_DEFAULT_SORT);
      setPeriodSort(k.sort);
      return lost + k.lost;
    },
    describe: (v) => describeView(v, SELLER_DIMS),
    note: t('Période, grain, mesure, filtres et tri — tout est repris. Les vues sont partagées avec l’équipe.'),
  };

  /**
   * LES VUES DU REPORTING VENDEURS. ⚠️ LA BARRE EST PARTAGÉE : la période et les filtres d'une vue valent pour tous les
   * onglets, comme s'ils avaient été choisis à la main ; ses colonnes deviennent celles du compte.
   */
  const reportViews: StatsViewAdapter = {
    snapshot: () => ({ a: settings.a, b: settings.b, q: settings.q, filters: settings.filters, axes: ['seller'], sort, display, columns: columns.layout }),
    apply: (v) => {
      setSettings({ a: v.a, b: v.b ?? null, q: v.q, filters: v.filters });
      setDisplay(v.b ? (v.display ?? 'd') : 'a');
      const keys = columns.pickerColumns.map((c) => c.key);
      const k = keptSort(v.sort, 1, keys, REPORT_DEFAULT_SORT);
      setSort(k.sort);
      const c = keptColumns(v.columns, keys);
      if (c) columns.change(c.layout);
      return k.lost + (c?.lost ?? 0);
    },
    describe: (v) => describeView(v, SELLER_DIMS),
    note: t('Périodes comparées, filtres, colonnes et tri — tout est repris. Les vues sont partagées avec l’équipe.'),
  };

  const pick = (id: TabId) => {
    const next = new URLSearchParams(params);
    if (id === 'resume') next.delete('onglet'); else next.set('onglet', id);
    setParams(next, { replace: true });
  };

  return (
    <>
      <div>
        <h1 style={{ margin: '0 0 10px', fontSize: 'var(--t-h1)', fontWeight: 700, letterSpacing: '-0.025em', color: 'var(--ink-900)' }}>{t('Vendeurs')}</h1>
        <Tabs size="universe" label={t('Statistiques des vendeurs')} panelId="stats-vendeurs-panel" value={tab} onChange={pick}
          items={TABS.map((x) => ({ id: x.id, label: t(x.label), icon: x.icon }))} />
      </div>
      <div id="stats-vendeurs-panel" role="tabpanel">
        {tab === 'resume' ? (
          <>
            <p style={{ margin: '3px 0 16px', fontSize: 'var(--t-title)', color: 'var(--ink-500)' }}>
              <Trans k='Qui fait le chiffre, <1>comment chacun vend</1>, et quels jours : de quoi animer l’équipe sans juger à l’instinct.'
                parts={[(x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>]} />
            </p>
            <StatsBar value={settings} onChange={changeSettings} today={today}
              scope={view ? t('{vendeurs} · {articles}', { vendeurs: t('{n} vendeur', { n: view.sellers.length }), articles: t('{n} article', { n: view.scope.references }) }) : ''}
              periodNote={asOfNote(view?.asOf ?? null, view?.period, view?.reference)}
              options={view?.filterOptions ?? {}} seasons={view?.seasonPeriods ?? []} />
            {!gln && (
              <div className="dcard"><EmptyState icon="store" title={t('Aucun magasin actif')} note={t('Choisissez un magasin pour lire ses ventes.')} /></div>
            )}
            {gln && status.phase === 'failed' && <ReadFailed message={status.message} />}
            {gln && !view && status.phase === 'loading' && <SellersPlaceholder />}
            {view && (
              // ⚠️ PENDANT UNE LECTURE, LES CHIFFRES D'AVANT SONT VOILÉS ET DITS : ils ne sont plus ceux des réglages affichés.
              <div aria-busy={busy} style={{ opacity: busy ? 0.55 : 1, transition: 'opacity .15s' }}>
                <SellersResume view={view} lang={currentLang()} />
              </div>
            )}
          </>
        ) : tab === 'reporting' ? (
          <>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 14, marginBottom: 16, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 250 }}>
                <p style={{ margin: '3px 0 0', fontSize: 'var(--t-title)', color: 'var(--ink-500)' }}>
                  <Trans k='Les mêmes indicateurs que Reporting ventes, mais <1>une ligne par vendeur</1>. Les filtres restreignent le périmètre ; la ventilation, elle, est toujours le vendeur.'
                    parts={[(x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>]} />
                </p>
              </div>
              <button type="button" onClick={() => { if (report && !reportBusy) exportReport(report, columns.visible, display, sort, currentLang(), 'reporting-vendeurs'); }}
                disabled={!report || reportBusy} title={t('On exporte ce que l’on voit : filtres, colonnes, tri')} className="oz-stats-export"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 7, height: 36, padding: '0 13px', border: '1px solid var(--ink-200)',
                  borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-700)', fontSize: 'var(--t-body)', fontWeight: 600,
                  whiteSpace: 'nowrap', cursor: report && !reportBusy ? 'pointer' : 'default', opacity: report && !reportBusy ? 1 : 0.55,
                }}>
                <Icon name="download" size={14} stroke={1.8} />{t('Export Excel')}
              </button>
            </div>
            <div style={{ background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)' }}>
              <StatsBar value={settings} onChange={changeSettings} today={today} framed={false} compare="chosen"
                scope={report ? reportScope(report) : ''} periodNote={asOfNote(report?.asOf ?? null, report?.period, report?.reference)}
                options={report?.filterOptions ?? {}} seasons={report?.seasonPeriods ?? []}
                periodEnd={settings.b ? <ReportDisplayToggle value={display} onChange={setDisplay} /> : undefined}
                filtersEnd={<>
                  <ReportColumnsButton columns={columns.pickerColumns} layout={columns.layout} onChange={columns.change} />
                  <StatsViewsButton key={gln ?? ''} tab="seller-report" enabled={!!gln} adapter={reportViews} />
                </>}>
                <StatsFixedLinesRow label={dimLabel(SELLER_DIMS[0]!)} icon={SELLER_DIMS[0]!.ic} hint={t('Ici, la ventilation EST le vendeur')}
                  legend={reportLegend(columns.visible)} />
              </StatsBar>
              {!gln && <EmptyState icon="store" title={t('Aucun magasin actif')} note={t('Choisissez un magasin pour lire ses ventes.')} />}
              {gln && reportStatus.phase === 'failed' && <ReadFailed message={reportStatus.message} framed={false} />}
              {gln && !report && reportStatus.phase === 'loading' && (
                <EmptyState icon="chart" title={t('Mesure en cours…')} note={t('Les indicateurs sont calculés sur les pièces fiscales du magasin.')} />
              )}
              {report && (
                <StatsReportTable view={report} visible={columns.visible} display={display} sort={sort} onSort={setSort} busy={reportBusy}
                  label={t('Vendeurs reporting')}
                  filtered={!!settings.q.trim() || Object.values(settings.filters).some((v) => v && v.length)}
                  onReset={() => changeSettings({ ...settings, q: '', filters: {} })} lang={currentLang()} />
              )}
            </div>
          </>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 14, marginBottom: 16, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 250 }}>
                <p style={{ margin: '3px 0 0', fontSize: 'var(--t-title)', color: 'var(--ink-500)' }}>
                  <Trans k='Qui vend quoi, <1>semaine après semaine</1> — la mesure de votre choix, éclatée dans le temps, une ligne par vendeur.'
                    parts={[(x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>]} />
                </p>
              </div>
              <button type="button" onClick={() => { if (periods && !periodBusy) exportSellerPeriods(periods, measure, periodSort, currentLang()); }}
                disabled={!periods || periodBusy} title={t('On exporte ce que l’on voit : filtres, mesure, tri')} className="oz-stats-export"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 7, height: 36, padding: '0 13px', border: '1px solid var(--ink-200)',
                  borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-700)', fontSize: 'var(--t-body)', fontWeight: 600,
                  whiteSpace: 'nowrap', cursor: periods && !periodBusy ? 'pointer' : 'default', opacity: periods && !periodBusy ? 1 : 0.55,
                }}>
                <Icon name="download" size={14} stroke={1.8} />{t('Export Excel')}
              </button>
            </div>
            <div style={{ background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)' }}>
              <StatsBar value={settings} onChange={changeSettings} today={today} framed={false} compare="none"
                scope={periods ? t('{vendeurs} · {articles}', { vendeurs: t('{n} vendeur', { n: periods.scope.rows }), articles: t('{n} article', { n: periods.scope.references }) }) : ''}
                periodNote={asOfNote(periods?.asOf ?? null)}
                options={periods?.filterOptions ?? {}} seasons={periods?.seasonPeriods ?? []}
                periodEnd={<SellerPeriodsControls measure={measure} onMeasure={setMeasure} grain={grain} onGrain={changeGrain} />}
                filtersEnd={<StatsViewsButton key={gln ?? ''} tab="seller-periods" enabled={!!gln} adapter={periodViews} />}>
                <StatsFixedLinesRow label={dimLabel(SELLER_DIMS[0]!)} icon={SELLER_DIMS[0]!.ic} hint={t('Cet écran ventile par vendeur')} legend={[]} />
              </StatsBar>
              {!gln && <EmptyState icon="store" title={t('Aucun magasin actif')} note={t('Choisissez un magasin pour lire ses ventes.')} />}
              {gln && periodStatus.phase === 'failed' && <ReadFailed message={periodStatus.message} framed={false} />}
              {gln && !periods && periodStatus.phase === 'loading' && (
                <EmptyState icon="chart" title={t('Mesure en cours…')} note={t('Les indicateurs sont calculés sur les pièces fiscales du magasin.')} />
              )}
              {periods && (
                <SellerPeriodsTable view={periods} measure={measure} sort={periodSort} onSort={setPeriodSort} busy={periodBusy}
                  filtered={!!settings.q.trim() || Object.values(settings.filters).some((v) => v && v.length)}
                  onReset={() => changeSettings({ ...settings, q: '', filters: {} })} lang={currentLang()} />
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}

/** Le Résumé des vendeurs : cinq indicateurs, qui fait le chiffre, comment chacun vend, la course, et quels jours. */
function SellersResume({ view, lang }: { view: SalesSellersView; lang: string }) {
  const refName = view.reference.kind === 'n1' ? t('N-1') : t('Comparaison');
  const tip = (v: string) => (view.reference.kind === 'n1' ? t('N-1 : {valeur}', { valeur: v }) : t('Comparaison : {valeur}', { valeur: v }));
  const a = view.totals;
  const r = view.refTotals;
  const series = (f: (x: SalesSellersView['totals']) => number | null) => view.buckets.map((b) => f(b.totals));
  const ticketsOf = (n: number): string => t('{n} ticket', { n });
  return (
    <>
      <div style={KPI_ROW}>
        <KpiCard label={t('CA net TTC')} icon="trendUp" tone="teal" value={eur0(a.netTtcCents)} growth={view.growth.netTtc}
          refLabel={tip(eur0(r.netTtcCents))} sub={t('{n} vendeur actif', { n: view.activeSellers })} series={series((x) => x.netTtcCents)} />
        <KpiCard label={t('Ventes')} icon="receipt" tone="blue" value={ticketsOf(a.tickets)} growth={view.growth.tickets}
          refLabel={tip(ticketsOf(r.tickets))} sub={t('Sur la période, retours compris')} series={series((x) => x.tickets)} />
        <KpiCard label={t('Panier moyen')} icon="basket" tone="blue" value={eur0(a.basketCents)} growth={view.growth.basket}
          refLabel={tip(eur0(r.basketCents))}
          sub={view.basketSpreadCents === null ? t('par ticket') : t('écart vendeurs {montant}', { montant: eur0(view.basketSpreadCents) })} series={series((x) => x.basketCents)} />
        <KpiCard label={t('Indice de vente')} icon="layers" tone="violet" value={a.idvX100 === null ? '—' : nombre(a.idvX100 / 100, { fractionDigits: 2 })}
          growth={view.growth.idv} refLabel={tip(r.idvX100 === null ? '—' : nombre(r.idvX100 / 100, { fractionDigits: 2 }))} sub={t('articles par ticket')} series={series((x) => x.idvX100)} />
        <KpiCard label={t('Taux de remise')} icon="percent" tone="rose" value={pc(a.discountRateBp)} growth={view.growth.discount} inverse
          refLabel={tip(pc(r.discountRateBp))} sub={t('{montant} accordés', { montant: eur0(a.grossTtcCents - a.netTtcCents) })} series={series((x) => x.discountRateBp)} />
      </div>
      <div style={CARD_ROW}>
        <Leaderboard view={view} refName={refName} />
        <Profile view={view} />
      </div>
      <Race view={view} lang={lang} />
      <Weekdays view={view} lang={lang} />
    </>
  );
}

/** QUI FAIT LE CHIFFRE — les vendeurs du plus gros chiffre au plus petit, leur part et leur écart. */
function Leaderboard({ view, refName }: { view: SalesSellersView; refName: string }) {
  const { tipProps, tip } = useChartTip();
  const max = Math.max(1, ...view.sellers.map((s) => s.totals.netTtcCents));
  const best = view.sellers[0];
  const prog = view.sellers.find((s) => s.id === view.bestGrowthId);
  const insight = !best || view.totals.netTtcCents <= 0 ? t('Aucune vente sur la période.')
    : [t('{vendeur} fait {part} du CA', { vendeur: best.name, part: pc(best.shareBp) }),
      prog && prog.id !== best.id ? t('{vendeur} progresse le plus ({ecart})', { vendeur: prog.name, ecart: dTx(prog.growthBp) }) : null].filter(Boolean).join(' · ');
  return (
    <div style={{ ...CARD, flex: '1 1 440px', minWidth: 'min(100%,420px)' }}>
      <CardHead icon="trophy" tone="teal" title={t('Qui fait le chiffre')} insight={insight} />
      {view.sellers.length ? (
        <div role="list" style={{ padding: '4px 16px 14px', display: 'flex', flexDirection: 'column', gap: 6 }}>
          {view.sellers.map((s, i) => {
            const c = chip(s.growthBp);
            return (
              <div key={s.id} role="listitem" style={{ display: 'grid', gridTemplateColumns: '20px 30px minmax(0,1fr) 84px 52px 64px', gap: 10, alignItems: 'center' }}>
                <span style={{ fontSize: 'var(--t-dense)', fontWeight: 700, color: 'var(--ink-500)', fontVariantNumeric: 'tabular-nums' }}>{i + 1}</span>
                <Avatar name={s.name} tone={toneOf(i)} />
                <span style={{ minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: 'var(--t-body)', fontWeight: 700, color: 'var(--ink-900)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.name}</span>
                  <span style={{ display: 'block', height: 6, marginTop: 4, borderRadius: 'var(--r-pill)', background: 'var(--ink-100)', overflow: 'hidden' }}>
                    <span style={{ display: 'block', height: '100%', width: `${Math.max(1, (Math.max(0, s.totals.netTtcCents) / max) * 100).toFixed(1)}%`, background: TONES[toneOf(i)][2] }} />
                  </span>
                  <span style={{ display: 'block', marginTop: 3, fontSize: 'var(--t-meta)', color: 'var(--ink-500)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {t('{ventes} · panier {montant}', { ventes: t('{n} vente', { n: s.totals.tickets }), montant: eur0(s.totals.basketCents) })}
                  </span>
                </span>
                <strong style={{ textAlign: 'right', fontSize: 'var(--t-body)', color: 'var(--ink-900)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{eur0(s.totals.netTtcCents)}</strong>
                <span style={{ textAlign: 'right', fontSize: 'var(--t-dense)', color: 'var(--ink-600)', fontVariantNumeric: 'tabular-nums' }}>{pc(s.shareBp)}</span>
                <span role="img" {...tipProps({ label: s.name, value: dTx(s.growthBp), detail: `${refName} ${eur0(s.refNetTtcCents)}` })} style={{
                  justifySelf: 'end', display: 'inline-flex', alignItems: 'center', height: 20, padding: '0 7px', borderRadius: 'var(--r-pill)', background: c.bg,
                  color: c.fg, fontSize: 'var(--t-meta)', fontWeight: 700, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
                }}>{dTx(s.growthBp)}</span>
              </div>
            );
          })}
          {tip}
        </div>
      ) : (
        <div style={EMPTY}>{t('Aucune vente sur la période.')}</div>
      )}
    </div>
  );
}

/** La pastille d'un vendeur : ses initiales, dans sa teinte. */
export function Avatar({ name, tone, size = 28 }: { name: string; tone: Tone; size?: number }) {
  return (
    <span aria-hidden style={{
      width: size, height: size, borderRadius: 'var(--r-pill)', background: TONES[tone][0], color: TONES[tone][1], fontSize: 'var(--t-dense)', fontWeight: 800,
      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
    }}>{initials(name)}</span>
  );
}

/** Les quatre façons de vendre, chacune lue dans les totaux d'un vendeur et du magasin. */
const PROFILE: { label: Translatable; of: (x: SalesSellerRow['totals']) => number | null; show: (v: number | null) => string }[] = [
  { label: key('Panier moyen'), of: (x) => x.basketCents, show: eur0 },
  { label: key('Indice de vente'), of: (x) => x.idvX100, show: (v) => (v === null ? '—' : nombre(v / 100, { fractionDigits: 2 })) },
  { label: key('Taux de remise'), of: (x) => x.discountRateBp, show: pc },
  { label: key('Taux de marge'), of: (x) => x.marginRateBp, show: pc },
];

/** COMMENT CHACUN VEND — chaque vendeur face à la moyenne du magasin, sur quatre mesures. */
function Profile({ view }: { view: SalesSellersView }) {
  const { tipProps, tip } = useChartTip();
  const rem = view.sellers.filter((s) => s.totals.discountRateBp !== null).sort((x, y) => y.totals.discountRateBp! - x.totals.discountRateBp!)[0];
  const baskets = view.sellers.map((s) => s.totals.basketCents).filter((x): x is number => x !== null);
  const insight = !rem ? t('Aucune vente sur la période.')
    : [t('{vendeur} remise le plus : {taux} (moy. {moyenne})', { vendeur: rem.name, taux: pc(rem.totals.discountRateBp), moyenne: pc(view.totals.discountRateBp) }),
      baskets.length > 1 ? t('panier de {min} à {max}', { min: eur0(Math.min(...baskets)), max: eur0(Math.max(...baskets)) }) : null].filter(Boolean).join(' · ');
  return (
    <div style={{ ...CARD, flex: '1 1 440px', minWidth: 'min(100%,420px)' }}>
      <CardHead icon="sliders" tone="violet" title={t('Comment chacun vend')} insight={insight} />
      {view.sellers.length ? (
        <div style={{ padding: '6px 16px 14px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          {PROFILE.map((m) => {
            const avg = m.of(view.totals);
            const vals = view.sellers.map((s) => m.of(s.totals));
            const all = [...vals, avg].filter((x): x is number => x !== null);
            const lo = all.length ? Math.min(...all) : 0;
            const hi = all.length ? Math.max(...all) : 1;
            const pad = Math.max((hi - lo) * 0.18, Math.abs(avg ?? hi) * 0.1, 1);
            const L = lo - pad;
            const H = hi + pad;
            const x = (v: number) => `${(((v - L) / (H - L)) * 100).toFixed(2)}%`;
            return (
              <div key={t(m.label)}>
                <div style={{ display: 'flex', justifyContent: 'space-between', ...MONO_LABEL }}>
                  <span>{t(m.label)}</span><span>{t('moy. {valeur}', { valeur: m.show(avg) })}</span>
                </div>
                <div style={{ position: 'relative', height: 38, marginTop: 4, borderBottom: '1px solid var(--ink-200)' }}>
                  {avg !== null && <span aria-hidden style={{ position: 'absolute', left: x(avg), top: 0, bottom: 0, borderLeft: '2px dashed var(--ink-400)' }} />}
                  {view.sellers.map((s, i) => {
                    const v = vals[i] ?? null;
                    return v === null ? null : (
                      <span key={s.id} role="img" {...tipProps({ label: s.name, value: m.show(v), detail: t('moy. {valeur}', { valeur: m.show(avg) }) })} style={{
                        position: 'absolute', left: x(v), top: i % 2 ? 19 : 3, transform: 'translateX(-50%)', zIndex: 10 + i,
                      }}><Avatar name={s.name} tone={toneOf(i)} size={18} /></span>
                    );
                  })}
                </div>
              </div>
            );
          })}
          {tip}
        </div>
      ) : (
        <div style={EMPTY}>{t('Aucune vente sur la période.')}</div>
      )}
    </div>
  );
}

const GRAIN_NOUN: Record<SalesSellersView['grain'], Translatable> = { day: key('jour'), week: key('semaine'), month: key('mois') };

/** LA COURSE AU CHIFFRE — le chiffre de chaque vendeur, cumulé (par défaut) ou par seau ; un clic masque un vendeur. */
function Race({ view, lang }: { view: SalesSellersView; lang: string }) {
  const [cumulative, setCumulative] = useState(true);
  const [hidden, setHidden] = useState<ReadonlySet<string>>(new Set());
  const { tipProps, tip } = useChartTip();
  const n = view.buckets.length;
  const visible = view.sellers.filter((s) => !hidden.has(s.id));
  const run = (vals: number[]) => { let s = 0; return vals.map((v) => (cumulative ? (s += v) : v)); };
  const series = new Map(view.sellers.map((s) => [s.id, run(s.buckets)]));
  const top = niceTop(Math.max(0, ...visible.flatMap((s) => series.get(s.id)!)));
  const X = (i: number) => ((i + 0.5) / n) * 1000;
  const Y = (v: number) => 1000 - (Math.max(0, v) / top) * 1000;
  const labels = view.buckets.map((b) => bucketLabels(b.key, view.grain, lang));
  const step = Math.max(1, Math.ceil(n / 7));
  const noun = t(GRAIN_NOUN[view.grain]);
  const toggle = (id: string) => setHidden((h) => {
    const next = new Set(h);
    // ⚠️ AU MOINS UN VENDEUR RESTE AFFICHÉ : une course vide ne dirait rien.
    if (next.has(id)) next.delete(id); else if (view.sellers.length - next.size > 1) next.add(id);
    return next;
  });
  const insight = n === 0 ? t('Aucun jour ouvré dans la période choisie.')
    : `${cumulative ? t('Course au chiffre sur la période') : t('Chiffre par {grain}', { grain: noun })} · ${t('cliquez un vendeur pour le masquer')}`;
  return (
    <div style={{ ...CARD, marginBottom: 13 }}>
      <CardHead icon="trendUp" tone="blue" title={t('La course au chiffre')} insight={insight}>
        <Seg label={t('Mode d’affichage')} value={cumulative ? '1' : '0'} onChange={(v) => setCumulative(v === '1')}
          options={[{ v: '0', l: t('Par {grain}', { grain: noun }) }, { v: '1', l: t('Cumulé') }]} />
      </CardHead>
      {n > 0 ? (
        <>
          <div style={{ padding: '8px 16px 0' }}>
            <div style={{ position: 'relative', height: 220, marginLeft: 48 }}>
              {[0, 0.25, 0.5, 0.75, 1].map((f) => (
                <div key={f}>
                  <div style={{ position: 'absolute', left: 0, right: 0, bottom: `${f * 100}%`, borderTop: '1px dashed var(--ink-200)' }} />
                  <span style={{
                    position: 'absolute', right: 'calc(100% + 8px)', transform: 'translateY(50%)', fontSize: 'var(--t-sub)', color: 'var(--ink-500)',
                    fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', bottom: `${f * 100}%`,
                  }}>{axisLabel(top * f, 'eur')}</span>
                </div>
              ))}
              <svg viewBox="0 0 1000 1000" preserveAspectRatio="none" aria-hidden
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', overflow: 'visible', pointerEvents: 'none' }}>
                {view.sellers.map((s, i) => (hidden.has(s.id) ? null : (
                  <path key={s.id} d={monotonePath(series.get(s.id)!.map((v, k) => [X(k), Y(v)]))} vectorEffect="non-scaling-stroke"
                    style={{ fill: 'none', stroke: TONES[toneOf(i)][2], strokeWidth: 2.4, strokeLinejoin: 'round', strokeLinecap: 'round' }} />
                )))}
              </svg>
              <div role="list" style={{ position: 'absolute', inset: 0, display: 'flex', zIndex: 3 }}>
                {view.buckets.map((b, k) => (
                  <div key={b.key} role="listitem" className="oz-stats-ev-col" {...tipProps({
                    label: labels[k]!.tip, value: visible.map((s) => `${s.name} ${axisLabel(series.get(s.id)![k] ?? 0, 'eur')}`).join(' · '),
                  })} style={{ flex: 1, minWidth: 0, height: '100%', borderRadius: 4 }} />
                ))}
              </div>
              {tip}
            </div>
            <div style={{ position: 'relative', height: 24, marginLeft: 48 }}>
              {labels.map((l, k) => (k % step === 0 ? (
                <span key={view.buckets[k]!.key} style={{
                  position: 'absolute', top: 7, left: `${((k + 0.5) / n) * 100}%`, transform: 'translateX(-50%)', fontSize: 'var(--t-sub)',
                  color: 'var(--ink-500)', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums',
                }}>{l.axis}</span>
              ) : null))}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px 16px', flexWrap: 'wrap', padding: '9px 16px 13px', marginTop: 4, borderTop: '1px solid var(--ink-100)' }}>
            {view.sellers.map((s, i) => (
              <button key={s.id} type="button" aria-pressed={!hidden.has(s.id)} onClick={() => toggle(s.id)} style={{
                display: 'inline-flex', alignItems: 'center', gap: 7, border: 'none', background: 'transparent', padding: 0, cursor: 'pointer',
                fontSize: 'var(--t-note)', color: 'var(--ink-700)', opacity: hidden.has(s.id) ? 0.4 : 1,
              }}>
                <span style={{ width: 14, height: 3, borderRadius: 2, background: TONES[toneOf(i)][2] }} />
                <strong>{s.name}</strong> {eur0(s.totals.netTtcCents)}
              </button>
            ))}
          </div>
        </>
      ) : (
        <div style={EMPTY}>{t('Aucun jour ouvré dans la période choisie — élargissez les dates.')}</div>
      )}
    </div>
  );
}

/** QUI VEND QUEL JOUR — le chiffre de chaque vendeur par jour de semaine ; un jour sans vente se dit, jamais « repos ». */
function Weekdays({ view, lang }: { view: SalesSellersView; lang: string }) {
  const { tipProps, tip } = useChartTip();
  const max = Math.max(1, ...view.sellers.flatMap((s) => s.weekdays.map((d) => d.netTtcCents)));
  const levels = [10, 24, 42, 64, 88];
  const level = (v: number) => { if (v <= 0) return 0; const r = v / max; return r > 0.85 ? 88 : r > 0.65 ? 64 : r > 0.45 ? 42 : r > 0.25 ? 24 : 10; };
  const leader = view.dayLeader ? view.sellers.find((s) => s.id === view.dayLeader!.sellerId) : undefined;
  const insight = !leader || !view.dayLeader ? t('Aucune vente sur la période.')
    : t('{jour} : {vendeur} fait {part} du chiffre du jour', { jour: weekdayName(view.dayLeader.weekday, lang, 'long'), vendeur: leader.name, part: pc(view.dayLeader.shareBp) });
  const grid = `minmax(130px,1.4fr) repeat(${view.weekdays.length}, minmax(52px,1fr))`;
  return (
    <div style={CARD}>
      <CardHead icon="calendar" tone="teal" title={t('Qui vend quel jour')} insight={insight} />
      {view.sellers.length ? (
        <>
          <div style={{ padding: '6px 16px 4px', overflowX: 'auto' }}>
            <div role="table" aria-label={t('Qui vend quel jour')} style={{ minWidth: 480, display: 'flex', flexDirection: 'column', gap: 5 }}>
              <div role="row" style={{ display: 'grid', gridTemplateColumns: grid, gap: 5, ...MONO_LABEL }}>
                <span role="columnheader">{t('Vendeur')}</span>
                {view.weekdays.map((w) => <span key={w} role="columnheader" style={{ textAlign: 'center' }}>{weekdayName(w, lang, 'short')}</span>)}
              </div>
              {view.sellers.map((s, i) => (
                <div key={s.id} role="row" style={{ display: 'grid', gridTemplateColumns: grid, gap: 5, alignItems: 'center' }}>
                  <span role="cell" style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                    <Avatar name={s.name} tone={toneOf(i)} />
                    <span style={{ minWidth: 0 }}>
                      <span style={{ display: 'block', fontSize: 'var(--t-body)', fontWeight: 600, color: 'var(--ink-900)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.name}</span>
                      <span style={{ display: 'block', fontSize: 'var(--t-meta)', color: 'var(--ink-500)' }}>{t('{part} du CA', { part: pc(s.shareBp) })}</span>
                    </span>
                  </span>
                  {s.weekdays.map((d) => {
                    const lv = level(d.netTtcCents);
                    return (
                      <span key={d.weekday} role="cell" {...tipProps({
                        label: `${s.name} · ${weekdayName(d.weekday, lang, 'long')}`,
                        value: d.netTtcCents === 0 ? t('aucune vente ce jour-là') : eur0(d.netTtcCents),
                        detail: d.shareBp === null || d.netTtcCents === 0 ? undefined : t('{part} du CA du jour', { part: pc(d.shareBp) }),
                      })} style={{
                        height: 36, borderRadius: 6, background: lv ? `color-mix(in oklch, var(--teal-500) ${lv}%, var(--paper))` : 'var(--ink-50)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 'var(--t-dense)', fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                        color: lv >= 64 ? 'var(--ink-900)' : d.netTtcCents !== 0 ? 'var(--ink-700)' : 'var(--ink-400)',
                      }}>{d.netTtcCents !== 0 ? axisLabel(d.netTtcCents, 'eur') : '—'}</span>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '9px 16px 13px', fontSize: 'var(--t-dense)', color: 'var(--ink-500)' }}>
            <span>{t('Moins')}</span>
            {levels.map((lv) => <span key={lv} aria-hidden style={{ width: 16, height: 12, borderRadius: 3, background: `color-mix(in oklch, var(--teal-500) ${lv}%, var(--paper))` }} />)}
            <span>{t('Plus')}</span>
            <span style={{ marginLeft: 'auto' }}>{t('CA TTC par jour de la semaine')}</span>
          </div>
          {tip}
        </>
      ) : (
        <div style={EMPTY}>{t('Aucune vente sur la période.')}</div>
      )}
    </div>
  );
}

/** Le gabarit de la première lecture : les cinq tuiles à leur place, sans un chiffre. */
function SellersPlaceholder() {
  const tiles: { label: Translatable; icon: IconName; tone: Tone }[] = [
    { label: key('CA net TTC'), icon: 'trendUp', tone: 'teal' }, { label: key('Ventes'), icon: 'receipt', tone: 'blue' },
    { label: key('Panier moyen'), icon: 'basket', tone: 'blue' }, { label: key('Indice de vente'), icon: 'layers', tone: 'violet' },
    { label: key('Taux de remise'), icon: 'percent', tone: 'rose' },
  ];
  return (
    <div aria-busy="true" style={KPI_ROW}>
      {tiles.map((x) => (
        <div key={x.icon} style={{ ...KPI_TILE, paddingBottom: 13 }}>
          <KpiLabel label={t(x.label)} icon={x.icon} tone={x.tone} />
          <div style={{ marginTop: 10, height: 22, width: '60%', borderRadius: 6, background: 'var(--ink-100)' }} />
        </div>
      ))}
    </div>
  );
}

